// ** React Imports
import React, { Fragment, useState, forwardRef, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useParam, Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

// ** Reactstrap Imports
import {
    Row,
    Col,
    Container,
    Label,
    Spinner,
    Toast,
    Input,
    CardBody,
    CardTitle,
    CardHeader,
    FormFeedback,
} from 'reactstrap'

import { Button } from 'react-bootstrap'
import * as Icons from 'react-feather'
import * as yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import toast, { Toaster } from 'react-hot-toast'

// auth/input-password-toggle
import InputPasswordToggle from '../../components/input-password-toggle/index'

// Utility-Service
import { getToken, setUserSession } from '../../../utility/AuthService'

// API-Service
import { loginService } from '../../../services/authService'

// Redux-Settings
import {
    selectPostDetail,
    setLoginDetails,
} from '../../../redux/slices/auth.Slice'
import { setLanguageChoice } from '../../../redux/slices/language.Slice';

const LoginPage = () => {
    const dispatch = useDispatch()
    const recaptchaRef = useRef(null);
    const [isLoading, setLoading] = useState(false)
    const [captchaPass, setCaptchaPass] = useState(false);
    const [recaptchaResponse, setRecaptchaResponse] = useState();
    const token = getToken()
    const navigate = useNavigate()

    useEffect(() => {
        if (token) {
            navigate('/user/history')
        }
    })

    const LoginValidation = yup.object().shape({
        email: yup.string().email().required('Please enter email'),
        password: yup.string().required('Please enter password')
    })
    // ** Hooks
    const {
        reset,
        control,
        handleSubmit,
        formState: { errors }
    } = useForm({ mode: 'onChange', resolver: yupResolver(LoginValidation) })

    /**
    * @function handleLogin
    * @param (data)
    * @description handle log in api, store in local storage and cookies.
    */
    const handleLogin = async (data) => {
        // e.preventDefault();
        setLoading(true)
        try {
            const result = await loginService({
                email: data.email,
                password: data.password,
                repatcheResponse: recaptchaResponse,
                userType: 'User'
            });
            const { accessToken, refreshToken } = result.data.data.tokens;

            if (result?.data?.statusCode === 200) {
                reset()
                setLoading(false)
                const getData = result?.data.data

                if (getData?.tokens) {
                    setLoading(false)
                    toast.success('Login successfully')

                    setUserSession(accessToken, { ...getData?.user, language: 'English' }, refreshToken);
                    dispatch(setLoginDetails(getData?.user));
                    localStorage.setItem('accessToken', accessToken);
                    localStorage.setItem('refreshToken', refreshToken);
                    localStorage.setItem('userData', JSON.stringify({ ...getData?.user }));
                    
                    // Use smart redirect based on user journey state
                    // For now, redirect to history which will show appropriate modal
                    navigate('/user/history?journey=login');
                    dispatch(setLanguageChoice(getData?.user?.language))
                } else {
                    toast.error(result?.data?.data?.message || 'Something went wrong')
                    setLoading(false);
                };
            } else {
                recaptchaRef.current.reset()
                toast.error('Something went wrong!')
                setLoading(false)
            };
            return false;

        } catch (error) {
            recaptchaRef.current.reset()
            setLoading(false);
            toast.error(error?.response?.data?.message || 'Something went wrong')
        };

    }

    /**
    * @function onSubmit
    * @param (data)
    * @description to submit
    */
    const onSubmit = data => {
        if (Object.values(data).every(field => field.length > 0)) {
            handleLogin(data)
        }
    }

    /**
    * @function signUp
    * @param ()
    * @description to navigate signUp page
    */

    const signUp = () => {
        navigate('/register')
    }

    /**
        * @function onCaptchaChange
        * @params value
        * @description set the captcha value
    */
    const onCaptchaChange = (value) => {
        setRecaptchaResponse(value)
        if (value) {
            setCaptchaPass(true)
        } else {
            setCaptchaPass(false)
        }
    }


    return (
        <>
            <Fragment>
                <Container fluid className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
                    <div className="w-100">
                        <Row className="justify-content-center mb-4">
                            <Col xs={12} className='text-center'>
                                <h3 className='auth-subtitle mb-0'>Don't have an account? 
                                    <span className='sign-up-link ms-2' 
                                          onClick={signUp}
                                          style={{ 
                                              color: '#007bff', 
                                              cursor: 'pointer',
                                              textDecoration: 'underline'
                                          }}>
                                        Sign Up
                                    </span>
                                </h3>
                            </Col>
                        </Row>
                        <Row className="justify-content-center">
                            <div className="login-card col-lg-4 col-md-6 col-sm-8 col-11">
                                <div className="bg-white shadow-sm rounded-3 p-4 p-lg-5">
                                    <div className="text-center mb-4">
                                        <h1 className="fw-bold mb-2" style={{ color: '#2c3e50' }}>SIGN IN</h1>
                                        <div className="login-border" style={{ 
                                            height: '4px',
                                            width: '60px',
                                            background: '#007bff',
                                            margin: '0 auto'
                                        }}/>
                                    </div>
                                    <Toaster position="top-right" />
                                    <form className='auth-login-form' onSubmit={handleSubmit(onSubmit)}>
                                        <div className='mb-4'>
                                            <Label className='form-label fw-semibold' for='email'>
                                                Email
                                            </Label>
                                            <Controller
                                                id='email'
                                                name='email'
                                                defaultValue=''
                                                control={control}
                                                render={({ field }) => (
                                                    <Input 
                                                        {...field} 
                                                        type='email' 
                                                        placeholder='Enter registered email'
                                                        className="form-control-lg"
                                                        style={{ borderRadius: '8px' }}
                                                        invalid={errors.email && true} 
                                                    />
                                                )}
                                            />
                                            {errors.email && <FormFeedback>{errors.email.message}</FormFeedback>}
                                        </div>
                                        <div className='mb-3'>
                                            <Label className='form-label fw-semibold' for='password'>
                                                Password
                                            </Label>
                                            <Controller
                                                id='password'
                                                name='password'
                                                defaultValue=''
                                                control={control}
                                                render={({ field }) => (
                                                    <InputPasswordToggle 
                                                        {...field} 
                                                        placeholder='Password'
                                                        className="form-control-lg"
                                                        style={{ borderRadius: '8px' }}
                                                        invalid={errors.password && true} 
                                                    />
                                                )}
                                            />
                                            {errors.password && <FormFeedback>{errors.password.message}</FormFeedback>}
                                        </div>
                                        <div className="mb-4">
                                            <Link 
                                                className="text-end d-block text-decoration-none" 
                                                to='/forgotPassword'
                                                style={{ color: '#6c757d' }}
                                            >
                                                Forgot Password?
                                            </Link>
                                        </div>
                                        <div className='mb-4 d-flex justify-content-center'>
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                className='google-recaptcha'
                                                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                                onChange={onCaptchaChange}
                                            />
                                        </div>
                                        <div className='d-grid'>
                                            <Button 
                                                variant='primary'
                                                className="btn-lg"
                                                style={{
                                                    borderRadius: '8px',
                                                    padding: '0.8rem'
                                                }}
                                                disabled={!captchaPass || isLoading} 
                                                type="submit"
                                            >
                                                {isLoading ? (
                                                    <Spinner 
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                ) : null}
                                                {isLoading ? 'Signing in...' : 'Sign In'}
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </Row>
                    </div>
                </Container>
            </Fragment>
        </>
    )
}

export default LoginPage
